import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { type User, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/firebase';

interface AuthContextType {
  user: User | null;
  userId: string | null; // Firestore User document ID
  role: string | null;
  displayName: string | null;
  loading: boolean;
  isUnregisteredError: boolean;
  authError: any;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userId: null,
  role: null,
  displayName: null,
  loading: true,
  isUnregisteredError: false,
  authError: null,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUnregisteredError, setIsUnregisteredError] = useState(false);
  const [authError, setAuthError] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(true);

      if (currentUser) {
        try {
          const getUserProfile = httpsCallable(functions, 'getUserProfile');
          const result = await getUserProfile();
          const data = result.data as any;

          if (data.error) {
            console.error("Server returned error:", data);
            // Don't throw - directly set error state to preserve debugInfo
            setAuthError(data.debugInfo || data);
            setIsUnregisteredError(true);
            setUserId(null);
            setRole(null);
            setDisplayName(null);
          } else {
            setIsUnregisteredError(false);
            setAuthError(null);
            setUserId(data.userId || null);
            setRole(data.role || 'user');
            setDisplayName(data.displayName || '');
            console.log('DEBUG: getUserProfile returned data:', data);
            setUserId(data.userId || null);
            console.log('DEBUG: Set userId to:', data.userId || null);

            // Force token refresh to get new custom claims
            try {
              await currentUser.getIdToken(true);
              try {
                const idTokenResult = await currentUser.getIdTokenResult();
                console.log('DEBUG: idTokenResult.claims =', idTokenResult.claims);
              } catch (tErr) {
                console.error('DEBUG: failed to getIdTokenResult', tErr);
              }
            } catch (tokenError) {
              console.error("Error refreshing token:", tokenError);
            }
          }
        } catch (error: any) {
          console.error("Error fetching user profile:", error);
          console.error("Error details:", error.message, error.code, error.details);
          setAuthError(error); // Store the full error object
          if (error.code === 'functions/not-found') {
            setIsUnregisteredError(true);
          } else {
            setIsUnregisteredError(true); // Fail safe
          }
          setUserId(null);
          setRole(null);
          setDisplayName(null);
        }
      } else {
        setUserId(null);
        setRole(null);
        setDisplayName(null);
        setIsUnregisteredError(false);
        setAuthError(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, userId, role, displayName, loading, isUnregisteredError, authError }}>
      {children}
    </AuthContext.Provider>
  );
};
